import { Icon, Stack, Typography } from '@mui/material';
import { CalendarDoneIcon, InformationBlock, WarningBlock } from '@schooly/style';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { FC, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form-lts';
import { FormattedMessage } from 'react-intl';

import { TimetableStructureForm } from '../../../../../context/timetableStructure/WithTimetableStructure';
import { useSchool } from '../../../../../hooks/useSchool';
import {
  deriveRegistrationGuidance,
  RegistrationGuidance,
  RegistrationShape,
  resolveSelectedFrequencies,
} from '../../registration-helpers';

const lineKey = (slot: 'am' | 'pm', shape: RegistrationShape) =>
  `school-timetableStructure-registration-info-line-${slot}-${
    shape === 'lesson_registration' ? 'lessonRegistration' : 'registration'
  }`;

export const TimetableStructureRegistrationInfo: FC = () => {
  const { control } = useFormContext<TimetableStructureForm>();
  const { schoolId = '' } = useSchool();
  const ageGroupIds = useWatch({ control, name: 'age_group_ids' });

  const { ageGroups } = useAgeGroups({ schoolId, userType: SchoolUserRole.Student });

  const guidance: RegistrationGuidance = useMemo(
    () => deriveRegistrationGuidance(resolveSelectedFrequencies(ageGroups, ageGroupIds ?? [])),
    [ageGroups, ageGroupIds],
  );

  if (guidance.kind === 'empty') return null;

  const renderIcon = (color: string) => (
    <Stack
      width={40}
      height={40}
      bgcolor="white"
      borderRadius={8}
      justifyContent="center"
      alignItems="center"
    >
      <Icon sx={{ color, mt: 0.25 }}>
        <CalendarDoneIcon />
      </Icon>
    </Stack>
  );

  if (guidance.kind === 'conflict') {
    return (
      <WarningBlock direction="row" alignItems="flex-start" gap={1.5} sx={{ border: 'none' }}>
        {renderIcon('inherit')}
        <Stack gap={1.5} flex={1}>
          <Typography variant="h3" color="inherit">
            <FormattedMessage id="school-timetableStructure-registration-conflict-title" />
          </Typography>
          <Typography variant="h3" color="inherit">
            <FormattedMessage id="school-timetableStructure-registration-conflict-body" />
          </Typography>
          <Typography variant="h3" color="inherit">
            <FormattedMessage id="school-timetableStructure-registration-conflict-suggestion" />
          </Typography>
        </Stack>
      </WarningBlock>
    );
  }

  const lines: string[] = [lineKey('am', guidance.am)];
  if (guidance.pm) lines.push(lineKey('pm', guidance.pm));

  return (
    <InformationBlock alignItems="flex-start" sx={{ gap: 1.5 }} p={2}>
      {renderIcon('primary.main')}
      <Stack gap={0.5} flex={1}>
        <Typography variant="h3">
          <FormattedMessage id="school-timetableStructure-registration-info-title" />
        </Typography>
        <Stack component="ul" sx={{ m: 0, pl: 2.5 }} gap={0.25}>
          {lines.map((key) => (
            <Typography variant="h3" key={key} component="li">
              <FormattedMessage id={key} />
            </Typography>
          ))}
        </Stack>
        <Typography variant="h3">
          <FormattedMessage id="school-timetableStructure-registration-info-footer" />
        </Typography>
      </Stack>
    </InformationBlock>
  );
};
